"use client";

import { useQuery } from "@tanstack/react-query";
import { clientFetch } from "@/lib/api/client-fetch";
import { STALE_TIME_PROFILE_MS } from "@/lib/react-query/defaults";
import { queryKeys } from "@/lib/react-query/query-keys";

export const CURRENT_USER_QUERY_KEY = queryKeys.currentUser();

export type CurrentUser = {
  id: string;
  username: string;
  email: string | null;
  avatar_url: string | null;
  /** Paid subscription (active / trialing / in-period past_due). */
  is_pro: boolean;
  created_at: string | null;
  auth_provider: string | null;
  has_password: boolean;
};

type ApiSuccess<T> = { success: true; data: T };

async function fetchCurrentUser(): Promise<CurrentUser> {
  const res = await clientFetch("/api/auth/me");
  if (!res.ok) throw new Error("Not authenticated");
  const payload: ApiSuccess<CurrentUser> = await res.json();
  const d = payload.data;
  return {
    ...d,
    is_pro: Boolean(d.is_pro),
    has_password: Boolean(d.has_password),
  };
}

type UseCurrentUserOptions = {
  initialData?: CurrentUser | null;
};

export function useCurrentUser(options?: UseCurrentUserOptions) {
  const initialData = options?.initialData ?? undefined;

  return useQuery({
    queryKey: CURRENT_USER_QUERY_KEY,
    queryFn: fetchCurrentUser,
    initialData: initialData ?? undefined,
    staleTime: STALE_TIME_PROFILE_MS,
    retry: false,
    refetchOnWindowFocus: false,
  });
}
